import React, { useState, useContext } from 'react';
import { View, Text, TextInput, StyleSheet, TouchableOpacity, ScrollView, KeyboardAvoidingView, Platform } from 'react-native';
import { AuthContext } from '../context/AuthContext';
import { showAlert } from '../services/confirm';
import Colors from '../constants/Colors';

const theme = Colors.light;
const { spacing, radius } = Colors;
const USERNAME_PATTERN = /^[A-Za-z0-9]{7,}$/;
const PASSWORD_PATTERN = /^(?=.*[A-Za-z])(?=.*\d).{7,}$/;

export default function RegisterScreen({ navigation }) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('husband');
    const [partnerCode, setPartnerCode] = useState('');
    const [nickname, setNickname] = useState('');
    const { register } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);

    const handleRegister = async () => {
        const normalizedUsername = username.trim();
        const normalizedPartnerCode = partnerCode.trim();
        const normalizedNickname = nickname.trim();

        if (!normalizedUsername || !password) {
            showAlert('提示', '请输入用户名和密码');
            return;
        }

        if (!USERNAME_PATTERN.test(normalizedUsername)) {
            showAlert('提示', '用户名需为至少7位字母或数字，不能包含中文和特殊字符');
            return;
        }

        if (!PASSWORD_PATTERN.test(password)) {
            showAlert('提示', '密码需至少7位，且必须同时包含字母和数字，特殊字符可选');
            return;
        }

        setLoading(true);
        try {
            const result = await register(
                normalizedUsername,
                password,
                role,
                normalizedPartnerCode,
                normalizedNickname
            );
            if (!result.success) showAlert('注册失败', result.error);
        } catch (e) {
            showAlert('错误', '注册发生未知错误');
        } finally {
            setLoading(false);
        }
    };

    return (
        <KeyboardAvoidingView
            style={styles.container}
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
            <ScrollView
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
            >
                {/* Header */}
                <View style={styles.header}>
                    <Text style={styles.largeTitle}>注册账号</Text>
                    <Text style={styles.subtitle}>加入情侣评分系统</Text>
                </View>

                {/* Form Group 1: Account */}
                <Text style={styles.groupLabel}>账号信息</Text>
                <View style={styles.formGroup}>
                    <View style={styles.inputRow}>
                        <Text style={styles.inputLabel}>用户名</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="请输入"
                            placeholderTextColor={theme.muted}
                            value={username}
                            onChangeText={setUsername}
                            autoCapitalize="none"
                        />
                    </View>
                    <View style={styles.divider} />
                    <View style={styles.inputRow}>
                        <Text style={styles.inputLabel}>密码</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="请输入"
                            placeholderTextColor={theme.muted}
                            value={password}
                            onChangeText={setPassword}
                            secureTextEntry
                        />
                    </View>
                    <View style={styles.divider} />
                    <View style={styles.inputRow}>
                        <Text style={styles.inputLabel}>昵称</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="显示名称"
                            placeholderTextColor={theme.muted}
                            value={nickname}
                            onChangeText={setNickname}
                        />
                    </View>
                </View>
                <Text style={styles.accountFootnoteFirst}>用户名：仅字母/数字，至少7位。</Text>
                <Text style={styles.accountFootnote}>密码：至少7位，必须含字母+数字，特殊字符可选。</Text>

                {/* Form Group 2: Role */}
                <Text style={styles.groupLabel}>身份</Text>
                <View style={styles.formGroup}>
                    <TouchableOpacity
                        style={styles.optionRow}
                        onPress={() => setRole('husband')}
                        activeOpacity={0.6}
                    >
                        <Text style={styles.optionLabel}>我是男生 🙋‍♂️</Text>
                        {role === 'husband' && <Text style={styles.checkmark}>✓</Text>}
                    </TouchableOpacity>
                    <View style={styles.divider} />
                    <TouchableOpacity
                        style={styles.optionRow}
                        onPress={() => setRole('wife')}
                        activeOpacity={0.6}
                    >
                        <Text style={styles.optionLabel}>我是女生 🙋‍♀️</Text>
                        {role === 'wife' && <Text style={styles.checkmark}>✓</Text>}
                    </TouchableOpacity>
                </View>

                {/* Form Group 3: Pairing */}
                <Text style={styles.groupLabel}>配对（可选）</Text>
                <View style={styles.formGroup}>
                    <View style={styles.inputRow}>
                        <Text style={styles.inputLabel}>配对码</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="伴侣的6位配对码"
                            placeholderTextColor={theme.muted}
                            value={partnerCode}
                            onChangeText={setPartnerCode}
                            keyboardType="numeric"
                            maxLength={6}
                        />
                    </View>
                </View>
                <Text style={styles.groupFootnote}>如果伴侣已注册，输入对方的配对码即可绑定。也可以注册后再配对。</Text>

                {/* Submit */}
                <TouchableOpacity
                    style={[styles.primaryBtn, loading && styles.primaryBtnDisabled]}
                    onPress={handleRegister}
                    disabled={loading}
                    activeOpacity={0.7}
                >
                    <Text style={styles.primaryBtnText}>{loading ? '注册中...' : '完成注册'}</Text>
                </TouchableOpacity>

                {/* Back Link */}
                <TouchableOpacity
                    style={styles.backLink}
                    onPress={() => navigation.navigate('Login')}
                >
                    <Text style={styles.backLinkText}>已有账号？返回登录</Text>
                </TouchableOpacity>

                <View style={{ height: spacing.xxxl }} />
            </ScrollView>
        </KeyboardAvoidingView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.background,
    },
    scrollContent: {
        paddingHorizontal: spacing.xl,
        paddingTop: spacing.xxxl,
    },

    // Header
    header: {
        marginBottom: spacing.xxl,
    },
    largeTitle: {
        fontSize: 34,
        fontWeight: '700',
        color: theme.text,
        letterSpacing: 0.4,
    },
    subtitle: {
        fontSize: 15,
        color: theme.textSecondary,
        marginTop: spacing.xs,
        letterSpacing: -0.2,
    },

    // iOS grouped form
    groupLabel: {
        fontSize: 13,
        fontWeight: '400',
        color: theme.textSecondary,
        marginBottom: spacing.sm,
        marginLeft: spacing.lg,
        textTransform: 'uppercase',
        letterSpacing: -0.1,
    },
    formGroup: {
        backgroundColor: theme.card,
        borderRadius: radius.card,
        ...theme.shadow,
        marginBottom: spacing.xl,
        overflow: 'hidden',
    },
    inputRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: spacing.lg,
        minHeight: 44,
    },
    inputLabel: {
        fontSize: 17,
        color: theme.text,
        width: 70,
        fontWeight: '400',
        letterSpacing: -0.3,
    },
    input: {
        flex: 1,
        paddingVertical: 12,
        fontSize: 17,
        color: theme.text,
        textAlign: 'right',
        letterSpacing: -0.3,
    },
    divider: {
        height: StyleSheet.hairlineWidth,
        backgroundColor: theme.border,
        marginLeft: spacing.lg,
    },

    accountFootnoteFirst: {
        fontSize: 13,
        color: theme.textSecondary,
        marginTop: -spacing.lg,
        marginBottom: spacing.xs,
        marginLeft: spacing.lg,
        lineHeight: 18,
    },

    accountFootnote: {
        fontSize: 13,
        color: theme.textSecondary,
        marginTop: 0,
        marginBottom: spacing.md,
        marginLeft: spacing.lg,
        lineHeight: 18,
    },

    // Option row (role selector)
    optionRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: spacing.lg,
        paddingVertical: 12,
        minHeight: 44,
    },
    optionLabel: {
        fontSize: 17,
        color: theme.text,
        letterSpacing: -0.3,
    },
    checkmark: {
        fontSize: 17,
        color: theme.blue,
        fontWeight: '600',
    },

    // Footnote
    groupFootnote: {
        fontSize: 13,
        color: theme.textSecondary,
        marginTop: -spacing.lg,
        marginBottom: spacing.xl,
        marginLeft: spacing.lg,
        lineHeight: 18,
    },

    // Primary Button
    primaryBtn: {
        backgroundColor: theme.blue,
        borderRadius: radius.card,
        paddingVertical: 15,
        alignItems: 'center',
        marginBottom: spacing.lg,
    },
    primaryBtnDisabled: {
        opacity: 0.5,
    },
    primaryBtnText: {
        color: '#fff',
        fontSize: 17,
        fontWeight: '600',
        letterSpacing: -0.3,
    },

    // Back Link
    backLink: {
        alignItems: 'center',
        paddingVertical: spacing.md,
    },
    backLinkText: {
        fontSize: 15,
        color: theme.blue,
    },
});
